import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  ContentCopy as CopyIcon,
  Pix as PixIcon,
  Refresh as RefreshIcon,
  ReceiptLong as ReceiptIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";

import DataTable from "@/components/mui/DataTable";

const MONTHLY_VALUE = 90;
const AUTO_CHECK_INTERVAL_MS = 15000;

const statusConfig = {
  pending: { label: "Pendente", color: "warning" },
  approved: { label: "Pago", color: "success" },
  rejected: { label: "Rejeitado", color: "error" },
  cancelled: { label: "Cancelado", color: "default" },
};

const companyStatusConfig = {
  periodo_teste: { label: "Período de Teste", color: "warning" },
  em_dia: { label: "Em Dia", color: "success" },
  em_atraso: { label: "Em Atraso", color: "error" },
  bloqueada: { label: "Bloqueada", color: "error" },
};

const formatMoney = (value) =>
  `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

const parseDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDate = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString("pt-BR") : "-";
};

const getPixImageSrc = (base64) => {
  if (!base64) return null;
  const mimeType = base64.startsWith("PHN2Z") ? "image/svg+xml" : "image/png";
  return `data:${mimeType};base64,${base64}`;
};

const getReferenceDueDate = (company) =>
  company?.payment_due_date || company?.trial_ends_at || null;

const calculateDebt = (company) => {
  const dueDate = parseDate(getReferenceDueDate(company));
  if (!dueDate || company?.payment_status === "em_dia") {
    return { amount: 0, daysOverdue: 0, months: 0 };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  if (today <= dueDate) {
    return { amount: 0, daysOverdue: 0, months: 0 };
  }

  const daysOverdue = Math.ceil((today.getTime() - dueDate.getTime()) / 86400000);
  const months = Math.max(1, Math.ceil(daysOverdue / 30));

  return {
    amount: months * MONTHLY_VALUE,
    daysOverdue,
    months,
  };
};

export default function Billing({ user }) {
  const [company, setCompany] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [generatingPix, setGeneratingPix] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchBilling();
  }, []);

  useEffect(() => {
    const pendingPayment = payments.find((payment) => payment.status === "pending");
    if (!pendingPayment) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      checkPaymentStatus({ silent: true });
    }, AUTO_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [payments]);

  const fetchBilling = async () => {
    setLoading(true);
    try {
      const profileRes = await axios.get(`${API}/company/profile`);
      const currentCompany = profileRes.data;
      setCompany(currentCompany);

      const paymentsRes = await axios.get(`${API}/payments/companies/${currentCompany.id}`);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar mensalidade");
    } finally {
      setLoading(false);
    }
  };

  const pendingPayment = useMemo(
    () => payments.find((payment) => payment.status === "pending"),
    [payments]
  );

  const debt = useMemo(() => calculateDebt(company), [company]);
  const approvedPayments = payments.filter((payment) => payment.status === "approved");
  const paidTotal = approvedPayments.reduce(
    (total, payment) => total + Number(payment.amount || 0),
    0
  );

  const openPaymentDialog = async () => {
    setPixData(pendingPayment || null);
    setPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPixData(null);
    setGeneratingPix(false);
  };

  const handleGeneratePix = async () => {
    const companyId = company?.id || user?.company_id;
    if (!companyId) return;

    setGeneratingPix(true);
    try {
      const response = await axios.post(`${API}/payments/companies/${companyId}/generate-pix`);
      setPixData(response.data.payment || response.data);
      toast.success("Cobrança PIX gerada com sucesso!");
      await fetchBilling();
    } catch (error) {
      toast.error("Erro ao gerar cobrança PIX");
    } finally {
      setGeneratingPix(false);
    }
  };

  const handleCopyPix = () => {
    if (!pixData?.qr_code) return;

    navigator.clipboard.writeText(pixData.qr_code);
    toast.success("Código PIX copiado!");
  };

  const checkPaymentStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setCheckingPayment(true);
    }

    try {
      const response = await axios.post(`${API}/payments/check-status`);

      if (response.data.approved || response.data.company_payment_status === "em_dia") {
        toast.success("Pagamento confirmado!");
        await fetchBilling();
        closePaymentDialog();
        return;
      }

      if (response.data.payment) {
        setPixData(response.data.payment);
      }

      if (!silent) {
        toast.info("Pagamento ainda não identificado. Aguarde alguns instantes.");
      }
    } catch (error) {
      if (!silent) {
        toast.error("Não foi possível verificar o pagamento agora");
      }
    } finally {
      if (!silent) {
        setCheckingPayment(false);
      }
    }
  };

  const paginatedPayments =
    rowsPerPage > 0
      ? payments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : payments;

  const columns = [
    {
      id: "description",
      label: "Cobrança",
      render: (payment) => (
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {payment.description || "Mensalidade Stock Sistema"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vencimento: {formatDate(payment.due_date)}
          </Typography>
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (payment) => {
        const config = statusConfig[payment.status] || {
          label: payment.status,
          color: "default",
        };
        return <Chip label={config.label} color={config.color} size="small" />;
      },
    },
    {
      id: "amount",
      label: "Valor",
      align: "right",
      render: (payment) => (
        <Typography variant="body2" fontWeight={700}>
          {formatMoney(payment.amount)}
        </Typography>
      ),
    },
    {
      id: "paid_at",
      label: "Pago em",
      render: (payment) => formatDate(payment.paid_at),
    },
  ];

  if (loading && !company) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const companyStatus = companyStatusConfig[company?.payment_status] || {
    label: company?.payment_status || "-",
    color: "default",
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Mensalidade
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhe vencimento, pagamentos e cobrança da sua empresa.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PixIcon />} onClick={openPaymentDialog}>
          Pagar com PIX
        </Button>
      </Box>

      {debt.amount > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          Existe {formatMoney(debt.amount)} em aberto desde {formatDate(getReferenceDueDate(company))}.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 2,
          mb: 3,
        }}
      >
        <Card sx={{ borderRadius: 2, boxShadow: "0 10px 40px rgba(15,23,42,0.04)" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Chip label={companyStatus.label} color={companyStatus.color} />
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, boxShadow: "0 10px 40px rgba(15,23,42,0.04)" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Próximo Vencimento
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {formatDate(getReferenceDueDate(company))}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, boxShadow: "0 10px 40px rgba(15,23,42,0.04)" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Em Aberto
            </Typography>
            <Typography variant="h5" fontWeight={800} color={debt.amount ? "error.main" : "success.main"}>
              {formatMoney(debt.amount)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2, boxShadow: "0 10px 40px rgba(15,23,42,0.04)" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Pago
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {formatMoney(paidTotal)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: "0 10px 40px rgba(15,23,42,0.04)" }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>
              Histórico de mensalidades
            </Typography>
          </Box>

          <DataTable
            columns={columns}
            rows={paginatedPayments}
            loading={loading}
            pagination={{
              page,
              rowsPerPage,
              total: payments.length,
              onPageChange: (_event, newPage) => setPage(newPage),
              onRowsPerPageChange: (event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              },
              rowsPerPageOptions: [5, 10, 25, { label: "Todos", value: -1 }],
            }}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `billing-payment-${row.id}`}
            emptyMessage="Nenhuma mensalidade encontrada."
          />
        </CardContent>
      </Card>

      <Dialog open={paymentDialogOpen} onClose={closePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Pagamento da mensalidade</DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              {formatMoney(MONTHLY_VALUE)}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {company?.name}
            </Typography>

            {!pixData ? (
              <Button
                variant="contained"
                size="large"
                onClick={handleGeneratePix}
                disabled={generatingPix}
                startIcon={generatingPix ? <CircularProgress size={20} /> : <PixIcon />}
              >
                {generatingPix ? "Gerando..." : "Gerar QR Code PIX"}
              </Button>
            ) : (
              <Box>
                {pixData.qr_code_base64 && (
                  <Box display="flex" justifyContent="center" my={2}>
                    <Box
                      component="img"
                      src={getPixImageSrc(pixData.qr_code_base64)}
                      alt="QR Code PIX"
                      sx={{
                        maxWidth: 300,
                        width: "100%",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    />
                  </Box>
                )}

                {pixData.qr_code && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={pixData.qr_code}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PixIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyPix}
                      sx={{ mt: 2 }}
                    >
                      Copiar Código PIX
                    </Button>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={
                    checkingPayment ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />
                  }
                  onClick={() => checkPaymentStatus()}
                  disabled={checkingPayment}
                  sx={{ mt: 2 }}
                >
                  {checkingPayment ? "Verificando..." : "Já paguei - verificar"}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
